import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react'
import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react'
import type { Tone } from '../format'

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')
export { cx }

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:opacity-90',
  secondary: 'bg-surface-2 text-fg border border-border hover:bg-surface',
  ghost: 'text-fg hover:bg-surface-2',
  danger: 'bg-danger text-accent-fg hover:opacity-90',
}

export function Button({ variant = 'secondary', size = 'md', className, children, busy, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; busy?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-8 px-2.5 text-[13px]' : 'h-10 px-3.5 text-sm',
        VARIANT[variant],
        className,
      )}
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  )
}

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-muted',
  accent: 'bg-accent-soft text-accent',
}

const TONE_ICON: Record<Tone, typeof Info> = { ok: CheckCircle2, warn: AlertTriangle, danger: XCircle, neutral: Info, accent: Info }

/** Status badge: colour plus icon plus text, never colour alone. */
export function Badge({ tone, children, icon = true, className }: { tone: Tone; children: ReactNode; icon?: boolean; className?: string }) {
  const Icon = TONE_ICON[tone]
  return (
    <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-semibold', TONE_CLASS[tone], className)}>
      {icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {children}
    </span>
  )
}

export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone }) {
  return (
    <div className="rounded-lg border border-border bg-surface-2 p-3">
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className={cx('num mt-0.5 text-xl font-semibold', tone === 'danger' && 'text-danger', tone === 'warn' && 'text-warn', tone === 'ok' && 'text-ok')}>{value}</div>
      {sub && <div className="num mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  )
}

export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const id = useId()
  return (
    <section aria-labelledby={id} className="border-t border-border px-4 py-3 first:border-t-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 id={id} className="text-xs font-semibold uppercase tracking-wide text-muted">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  )
}

export function Callout({ tone, title, children }: { tone: Tone; title: string; children?: ReactNode }) {
  const Icon = TONE_ICON[tone]
  return (
    <div className={cx('flex gap-2 rounded-lg p-3 text-sm', TONE_CLASS[tone])} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="font-semibold">{title}</div>
        {children && <div className="mt-0.5 text-fg">{children}</div>}
      </div>
    </div>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-4 text-sm text-muted" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.'
  return (
    <div className="p-4">
      <Callout tone="danger" title="Could not load">
        {message}
        {onRetry && (
          <div className="mt-2">
            <Button size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}
      </Callout>
    </div>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="px-4 py-8 text-center">
      <div className="font-medium">{title}</div>
      {children && <div className="mt-1 text-sm text-muted">{children}</div>}
    </div>
  )
}

/** Two-colour swatch for a fibre: tube colour outer ring, fibre colour inner dot. */
export function FiberSwatch({ tube, fiber, title }: { tube: string; fiber: string; title: string }) {
  return (
    <span className="relative inline-block h-4 w-4 shrink-0 rounded-full border border-border" style={{ background: tube }} title={title} aria-hidden>
      <span className="absolute inset-[3px] rounded-full border border-black/20" style={{ background: fiber }} />
    </span>
  )
}

export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: { id: T; label: string; count?: number }[]; value: T; onChange: (t: T) => void; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto border-b border-border px-3">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cx(
            '-mb-px whitespace-nowrap border-b-2 px-2.5 py-2 text-sm font-medium',
            value === t.id ? 'border-accent text-accent' : 'border-transparent text-muted hover:text-fg',
          )}
        >
          {t.label}
          {t.count !== undefined && <span className="num ml-1 text-xs text-muted">{t.count}</span>}
        </button>
      ))}
    </div>
  )
}

export function KeyValue({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(0,40%)_1fr] gap-x-3 gap-y-1.5 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="num min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
